/**
 * Documents page — orchestrates table state, RPA actions, and modals
 * for the single-order documents tab.
 * @module Documents
 */

import { ButtonWithTooltip } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import CustomTable from "../../../../components/custom-table/custom-table";
import NoData from "../../../../components/noData/noData";
import ValidationMessageList from "../../../../components/validationMessageList/validationMessageList";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { JCodeDetails } from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import { useHealthCareFacility } from "../../../../hooks/useHealthCareFacility";
import { setOpenedModalId } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { handleDocumentScreenshotClick } from "../../../../utils/handleDocumentScreenshotClick";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import DocumentModals from "./components/DocumentModals";
import { CptCodesTableColumnsKeys } from "./documentsTable";
import { useDocumentRpaActions } from "./hooks/useDocumentRpaActions";
import { useDocumentRpaValidationMessages } from "./hooks/useDocumentRpaValidationMessages";
import { useDocumentTableState } from "./hooks/useDocumentTableState";

/**
 * Documents tab: loads CPT/J-code document rows, wires Trigger RPA and
 * screenshot actions, and mounts {@link DocumentModals} for doc workflows.
 *
 * @returns The documents worklist view for the current route `orderId`.
 */
const Documents = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const {
    generalOrderDetails,
    authOnFileDetails,
    authorizationData,
    authorizationDataLoading,
  } = useDataAccumulatorForSingleOrder();
  const unwrappedGeneralOrderDetails = generalOrderDetails?.order ?? null;
  const unwrappedAuthOnFileDetails = authOnFileDetails?.auth_on_file ?? null;

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { hasCptTableAnyError, shouldDisableMarkAsComplete } =
    useBusniessOfficeNotesContext();
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const { healthCareFacilityData: healthCareFacilityDataForOrg } =
    useHealthCareFacility(getOrgIdForFetchExternalWorklist());

  const { tableData, jcodes, handleTableRowUpdate } = useDocumentTableState({
    orderId,
    generalOrderDetails,
    unwrappedGeneralOrderDetails,
    unwrappedAuthOnFileDetails,
    authOnFileDetails,
    authorizationData,
    authorizationDataLoading,
  });

  const {
    loading,
    rpaBlockers,
    shouldHideTriggerRpaButton,
    handleSaveChanges,
  } = useDocumentRpaActions({
    unwrappedGeneralOrderDetails,
    unwrappedAuthOnFileDetails,
    generalOrderDetails,
    jcodes,
    user,
    payerName,
    healthCareFacilityData: healthCareFacilityDataForOrg,
    dispatch,
  });

  const { validationMessages, isTriggerRpaDisabled } =
    useDocumentRpaValidationMessages({
      rpaBlockers,
      hasCptTableAnyError,
      isFacilityDataLoading: healthCareFacilityDataForOrg?.loading ?? false,
    });

  const handleIconClickOnDocumentScreenshot = (rowData: JCodeDetails) => {
    handleDocumentScreenshotClick({
      rowData,
      screenshots:
        unwrappedAuthOnFileDetails?.screenshots?.document_upload_screenshots,
      dispatch,
    });
  };

  return (
    <div className="cpt-codes-table--container flex h-full flex-col">
      {tableData.length > 0 ? (
        <>
          {!shouldHideTriggerRpaButton && !shouldDisableMarkAsComplete && (
            <div className="trigger-rpa ml-auto w-fit">
              {" "}
              <ButtonWithTooltip
                showTooltip={isTriggerRpaDisabled}
                tooltipContent={
                  <ValidationMessageList messages={validationMessages} />
                }
                buttonType="secondary"
                size="medium"
                onClick={() => {
                  apiWorkerManager.enqueueAndProcess({
                    patient_id:
                      unwrappedGeneralOrderDetails?.demographics?.patient_id ??
                      unwrappedGeneralOrderDetails?.demographics?.patient_mrn ??
                      "",
                    order_id: unwrappedGeneralOrderDetails?.order_id ?? "",
                    org_id: getOrgIdForFetchExternalWorklist() || "",
                    event_name: EventsName.TRIGGER_RPA_CLICKED,
                    user_id: user?.id ?? "",
                    user_email: user?.email ?? "",
                    additional_data: {
                      screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
                      payer_name: payerName,
                    },
                  });
                  dispatch(
                    setOpenedModalId({
                      id: ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL,
                    }),
                  );
                }}
                disabled={isTriggerRpaDisabled}
              >
                Trigger RPA
              </ButtonWithTooltip>
            </div>
          )}
          <>
            <CustomTable
              tableHeaders={CptCodesTableColumnsKeys}
              tableData={tableData}
              itemsPerPage={tableData.length}
              pagesPerView={5}
              count={tableData.length}
              onRowClick={() => {}}
              tableName="cptCodes"
              hideSearchBar={true}
              hidePagination={true}
              onTableRowUpdate={handleTableRowUpdate}
              handleIconClickOnDocumentScreenshot={(rowData: JCodeDetails) => {
                handleIconClickOnDocumentScreenshot(rowData);
              }}
            />
            <DocumentModals
              openedModalId={openedModalId}
              orderId={orderId ?? ""}
              handleSaveChanges={handleSaveChanges}
              loading={loading}
              unwrappedAuthOnFileDetails={unwrappedAuthOnFileDetails}
              dispatch={dispatch}
            />
          </>
        </>
      ) : (
        <div className="flex h-full items-center justify-center rounded-lg bg-gray-50">
          <NoData />
        </div>
      )}
    </div>
  );
};

export default Documents;
